import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { t } from 'ember-intl';
import { dropTask } from 'ember-concurrency';
import Button from 'tio-ui/components/button';
import Select from 'tio-ui/components/select';
import ChecklistRow from './checklist-row';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';

interface SubmitEvidenceCourseRowSignature {
  Args: {
    course: TasCourseModel;
    grades: string[];
    onSave?: (course: TasCourseModel, grade: string) => Promise<void>;
    /** The template does not require grades, so this row is not counted. */
    isOptional?: boolean;
  };
  Element: HTMLDivElement;
}

/**
 * One course's grade-entry row in the Submit Evidence checklist (Figma:
 * "Add a grade for [Course Name]"). Owns the pending (unsaved) selection
 * locally; saving is immediate on click (mirrors the legacy show page's
 * onSelectionChange, but as an explicit button rather than save-on-select,
 * matching the Figma interaction). A saved grade stays editable — via an
 * "Edit" affordance — for as long as this row renders at all (the legacy
 * show page keeps grades editable throughout ATTEND; this row only ever
 * renders while the app is still in that same window).
 */
export default class SubmitEvidenceCourseRow extends Component<SubmitEvidenceCourseRowSignature> {
  @tracked pendingGrade: string | null = null;
  @tracked isEditing = false;

  get hasGrade() {
    return this.args.course.hasGrades;
  }

  get showEditor() {
    return !this.hasGrade || this.isEditing;
  }

  get selectedGrade() {
    return this.pendingGrade ?? this.args.course.courseGrade;
  }

  get isSaveDisabled() {
    return !this.pendingGrade || this.saveGrade.isRunning;
  }

  setPendingGrade = (grade: string | null) => {
    this.pendingGrade = grade;
  };

  startEdit = () => {
    this.isEditing = true;
  };

  saveGrade = dropTask(async () => {
    if (!this.pendingGrade || !this.args.onSave) {
      return;
    }
    await this.args.onSave(this.args.course, this.pendingGrade);
    this.pendingGrade = null;
    this.isEditing = false;
  });

  <template>
    <ChecklistRow
      @title={{t
        "tuition_assistance.applications.manage.tasks.add_grade_for_course"
        course=@course.displayName
      }}
      @hint={{if this.showEditor (t "tuition_assistance.applications.manage.tasks.add_grade_hint")}}
      @isDone={{this.hasGrade}}
      @isOptional={{@isOptional}}
      ...attributes
    >
      {{#if this.showEditor}}
        <div class="tio-row items-center gap-2">
          <Select
            @items={{@grades}}
            @selected={{this.selectedGrade}}
            @onChange={{this.setPendingGrade}}
            class="w-40"
            data-test-course-grade-select
          />
          <Button
            @intent="primary"
            @isDisabled={{this.isSaveDisabled}}
            @isRunning={{this.saveGrade.isRunning}}
            data-test-add-grade-cta
            {{on "click" this.saveGrade.perform}}
          >
            {{t "tuition_assistance.applications.manage.tasks.add_grade_cta"}}
          </Button>
        </div>
      {{else}}
        <div class="tio-row items-center gap-2">
          <p class="tio-text-sm-normal tio-fg-secondary" data-test-course-grade-value>
            {{@course.courseGrade}}
          </p>
          <Button
            @appearance="minimal"
            @intent="primary"
            @size="sm"
            data-test-edit-grade-cta
            {{on "click" this.startEdit}}
          >
            {{t "common.edit"}}
          </Button>
        </div>
      {{/if}}
    </ChecklistRow>
  </template>
}
